import React, { useState, useEffect } from 'react';
import { Clock, Play, Pause, RotateCcw, Coffee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DashboardLayout from '@/components/DashboardLayout';
import { toast } from 'sonner';

export default function StudyTimer() {
  const [minutes, setMinutes] = useState(25);
  const [seconds, setSeconds] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [mode, setMode] = useState('study'); // study or break
  const [completedSessions, setCompletedSessions] = useState(0);

  useEffect(() => {
    let interval = null;

    if (isActive) {
      interval = setInterval(() => {
        if (seconds === 0) {
          if (minutes === 0) {
            // Timer completed
            handleTimerComplete();
          } else {
            setMinutes(minutes - 1);
            setSeconds(59);
          }
        } else {
          setSeconds(seconds - 1);
        }
      }, 1000);
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [isActive, minutes, seconds]);

  const handleTimerComplete = () => {
    setIsActive(false);
    
    if (mode === 'study') {
      setCompletedSessions(completedSessions + 1);
      toast.success('Study session complete! Time for a break.');
      setMode('break');
      setMinutes(5);
      setSeconds(0);
    } else {
      toast.success('Break over! Ready for another study session?');
      setMode('study');
      setMinutes(25);
      setSeconds(0);
    }

    // Play notification sound
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('TopMarks Timer', {
        body: mode === 'study' ? 'Time for a break!' : 'Break over!',
        icon: '/logo.png'
      });
    }
  };

  const toggleTimer = () => {
    if (!isActive && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    setIsActive(!isActive);
  };

  const resetTimer = () => {
    setIsActive(false);
    setMode('study');
    setMinutes(25);
    setSeconds(0);
  };

  const startBreak = () => {
    setIsActive(false);
    setMode('break');
    setMinutes(5);
    setSeconds(0);
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 fade-in">
        <div className="bg-white rounded-lg border border-slate-200 p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Study Timer</h1>
          <p className="text-slate-600">Pomodoro technique for focused study sessions</p>
        </div>

        {/* Timer Display */}
        <div className="bg-gradient-to-br from-blue-900 to-indigo-900 rounded-lg p-12 text-center">
          <div className="inline-flex items-center justify-center space-x-4 mb-8">
            <div className={`px-6 py-3 rounded-full ${
              mode === 'study' ? 'bg-green-500' : 'bg-yellow-500'
            }`}>
              <p className="text-white font-bold">
                {mode === 'study' ? '📚 Study Time' : '☕ Break Time'}
              </p>
            </div>
          </div>

          <div className="text-9xl font-bold text-white mb-8 font-mono">
            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
          </div>

          <div className="flex justify-center space-x-4">
            <Button
              onClick={toggleTimer}
              className={`${
                isActive ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'
              } text-white px-8 py-6 text-lg`}
              data-testid="timer-toggle-btn"
            >
              {isActive ? (
                <>
                  <Pause className="w-5 h-5 mr-2" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Start
                </>
              )}
            </Button>

            <Button
              onClick={resetTimer}
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
              data-testid="timer-reset-btn"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Reset
            </Button>

            {mode === 'study' && (
              <Button
                onClick={startBreak}
                variant="outline"
                className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                data-testid="timer-break-btn"
              >
                <Coffee className="w-5 h-5 mr-2" />
                Break
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-2">Completed Today</p>
            <p className="text-4xl font-bold text-blue-900">{completedSessions}</p>
            <p className="text-xs text-slate-500 mt-2">Study sessions</p>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-2">Study Time</p>
            <p className="text-4xl font-bold text-green-600">{completedSessions * 25}</p>
            <p className="text-xs text-slate-500 mt-2">Minutes focused</p>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-2">Break Time</p>
            <p className="text-4xl font-bold text-yellow-600">{completedSessions * 5}</p>
            <p className="text-xs text-slate-500 mt-2">Minutes rested</p>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
          <h3 className="font-bold text-blue-900 mb-3">💡 Pomodoro Tips</h3>
          <ul className="space-y-2 text-sm text-slate-700">
            <li>• Study for 25 minutes with full focus (no distractions)</li>
            <li>• Take a 5-minute break after each session</li>
            <li>• After 4 sessions, take a longer 15-30 minute break</li>
            <li>• Use breaks to stretch, walk, or get water</li>
            <li>• Turn off notifications during study sessions</li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
